-- | What the verb drills are, as distinct from what they do.
module Flashcards.Pages.Verbs.Model
  ( Message(..)
  , Modal(..)
  , Phase(..)
  , Square
  , State
  , namespace
  , typing
  )
  where

import Prelude

import Data.DateTime.Instant (Instant)
import Data.Maybe (Maybe)
import Flashcards.Exercise (Exercise, Verdict)
import Flashcards.Moved as Moved
import Flashcards.Notes.Sheet as Notes
import Flashcards.Sync as Sync
import Flashcards.Types.Card (Slug)
import Flashcards.Types.Grade (Grade)
import Flashcards.Types.Progress (Progress)
import Flashcards.Verbs.Table (Tense)

-- | Its own namespace, so its progress is its own blob on the server and its
-- | own key in storage — `flashcards.verbs.v1` beside `flashcards.es.v1`, via
-- | `Storage.progressKey`, rather than a second place that spells the key out. The *pairing* key is shared, so a device paired for
-- | the flashcards is already paired for this — one key, both pages.
namespace :: String
namespace = "verbs"

type State =
  { progress :: Progress
  -- | What is left of the session, the item being asked first.
  , queue :: Array Slug
  -- | Which of the first item's pool is being asked. Held rather than
  -- | recomputed, because grading moves `seen` and `Exercise.pick` would then
  -- | change the question under the answer.
  , shown :: Maybe Exercise
  -- | This session's tally. Their sum is how many have been graded, and
  -- | until that is zero a sync bringing history in can rebuild the session
  -- | without taking anything back off the screen.
  , got :: Int
  , again :: Int
  -- | Where each item stood the first time this session answered it, for the
  -- | done screen to say what moved. See `Flashcards.Moved`.
  , before :: Moved.Before
  , typed :: String
  -- | How far the current question has got.
  , phase :: Phase
  -- | Whatever is over the question, if anything. See `Modal`.
  , modal :: Maybe Modal
  -- | When the page last knew the time: set as a session is built and again
  -- | on every grade. The done screen needs one to say when the next item
  -- | falls due, and the cards do the same with the moment their session
  -- | ended. Left behind if the page is sat on for an hour, which is the same
  -- | staleness the cards carry.
  , at :: Maybe Instant
  , syncKey :: Maybe String
  -- | What the server is known to hold. See the study page, where the same
  -- | comparison decides whether the panel may claim to be up to date.
  , sent :: Maybe Progress
  , offline :: Boolean
  , loaded :: Boolean
  }

-- | What the box says, once it is allowed to say anything. Only while the
-- | question is `Asked`: after the check the echo reads this field, so a
-- | change here would have the page contradict a verdict it has already given.
-- | `readOnly` keeps the DOM from sending one; this is what makes the echo true
-- | whatever the DOM does, and it is a function so that it can be tested.
typing :: String -> State -> State
typing text state = case state.phase of
  Asked -> state { typed = text }
  _ -> state

-- | What is over the question. One field rather than one per overlay, because
-- | only one is ever open: with a field each, every place that opened one had
-- | to clear the others by hand, and every place that asked whether the page
-- | was covered had to list them all — which is how #60 happened.
-- |
-- | Its own type rather than the study page's, which has more in it and
-- | spells the menu differently. The two pages share no state, and a shared
-- | type would be one more thing to keep both of them agreeing with.
data Modal
  -- | The ••• menu. The drills have one for the same reason the cards do: the
  -- | top bar is for the session, and anything that is not part of it goes
  -- | behind the dots.
  = Panel
  -- | The progress sheet, when it was opened, and which square of its grid
  -- | is open, if any. The moment is fixed on opening, as on the cards, so
  -- | *due tomorrow* does not move under you while you read it. The square
  -- | lives here rather than beside it because it means nothing once the
  -- | sheet has closed, and reopening should not find it still open.
  | Stats { at :: Instant, open :: Maybe Square }
  -- | The note being written. See `Flashcards.Notes`.
  | Note Notes.Open

-- | A square of the progress sheet's grid: a verb and a tense.
type Square = { infinitive :: String, tense :: Tense }

-- | Where the current question has got to.
-- |
-- | One field rather than a flag each, because the two kinds of answer end
-- | differently — a typed one is graded by the comparison, a self-graded one
-- | by the reader — and two booleans would have to be kept agreeing.
data Phase
  -- | Waiting: for something typed, or for a tap to reveal.
  = Asked
  -- | Typed and compared, and the grade followed from that.
  | Compared Verdict
  -- | Revealed, and waiting for the reader to say how it went.
  | Revealed
  -- | Self-graded, and the grade on its way to `Graded`. Here so that a
  -- | second tap on *Again* or *Got it* cannot grade twice — the typed side
  -- | gets that from `Compared`, which it reaches immediately; this side has
  -- | to wait for the clock, and would otherwise stay tappable meanwhile.
  | Judging

derive instance Eq Phase

data Message
  = Loaded { progress :: Progress, syncKey :: Maybe String }
  | Started Instant
  -- | Start another session, from the progress as it is now. For the done
  -- | screen, where the last one ended at its cap with more still due.
  | StartAnother
  -- | The tab is back in front. Only the done screen cares: its clock was set
  -- | when it was drawn.
  | Visible
  | Woke Instant
  -- | A key, not yet read as anything. See `keyMessage`.
  | Pressed String
  | Typed String
  -- | Tap an option, by its place in the list. Graded on the spot.
  | Choose Int
  -- | Check what was typed, or reveal what a self-graded answer was.
  | Answer
  -- | How the reader says they did, where nothing can check it for them.
  | Judge Grade
  | TogglePanel
  | ShowStats
  | StatsAt Instant
  | HideStats
  -- | Open a square of the grid, or close it if it is the one open.
  | OpenSquare Square
  | Graded Grade Instant
  | Next
  | Sync
  | Synced Sync.Remote
  | Pushed Progress Boolean
  | WriteNote
  | Notes Notes.Message
